/**
 * The one list of venue types on Aghosha.
 *
 * Every customer-facing page (home, venues/explore filters, venue page) and the
 * manager's "Add venue" form read their options from here, so a type can never
 * be offered in one place and missing in another. The server keeps the same
 * list as HALL_CATEGORIES in server.js (which validates it) and the Flutter app
 * as hallCategories — change all three together.
 *
 * "Marriage Hall" was a second name for "Wedding Hall" — the two are one type
 * (migration 016 moved every venue over), and the server maps the old name on
 * save.
 */
export const VENUE_TYPES = [
  'Auditorium',
  'Banquet Hall',
  'Convention Centre',
  'Wedding Hall',
  'Conference Hall',
  'Community Hall',
  'Party Hall',
  'Open-air Venue',
] as const;

export type VenueType = (typeof VENUE_TYPES)[number];

/** Older or informal names for the same type, mapped to the type they mean. */
const ALIASES: Record<string, VenueType> = {
  'marriage hall': 'Wedding Hall',
  'kalyana mandapam': 'Wedding Hall',
  'convention center': 'Convention Centre',
  'banquet': 'Banquet Hall',
};

/** A stored category as its canonical type; anything unknown is returned trimmed, unchanged. */
export function canonicalVenueType(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  const exact = VENUE_TYPES.find((v) => v.toLowerCase() === lower);
  return exact ?? ALIASES[lower] ?? s;
}

export const isVenueType = (v: string): v is VenueType => (VENUE_TYPES as readonly string[]).includes(v);
